import { HelpCircle } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/ui/dialog";
import { useAuthV2 } from "@/lib/authv2/store";
import { audiencesOf } from "./audience";
import { GuideBlockList, GuideBulletList, GuideStatusList } from "./GuideSections";
import type { PageGuide } from "./types";

type Props = {
  open: boolean;
  onOpenChange: (v: boolean) => void;
  title: string;
  guide: PageGuide;
  /**
   * Bỏ qua ở hộp chung — bài của nó không chia theo thẻ. Nhận vào để ba hộp
   * cùng một chữ ký, nhờ đó nút hướng dẫn chọn hộp bằng một biến chứ không
   * phải rẽ nhánh JSX ba lần.
   */
  guideTab?: string | undefined;
  /** Bỏ qua ở hộp chung; nhận vào cho cùng chữ ký với hộp nhiều thẻ. */
  guideId?: string | undefined;
};

/**
 * Hộp hướng dẫn của một trang.
 *
 * Chỉ dựng những khối hợp với nhóm nghề của người đang đăng nhập. Người không
 * khớp khối nào vẫn đọc được mục đích và vị trí trên màn — đủ để biết mình
 * đang đứng ở đâu, không đủ để học phần việc của phòng khác.
 */
export function GuideDialog({ open, onOpenChange, title, guide }: Props) {
  const staff = useAuthV2((s) => s.currentStaff);
  const auds = audiencesOf(staff);
  const blocks = (guide.blocks ?? []).filter((b) => b.for.some((a) => auds.includes(a)));

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="md:max-w-2xl">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <HelpCircle className="size-5 shrink-0 text-muted" aria-hidden />
            <span className="min-w-0">Hướng dẫn: {title}</span>
          </DialogTitle>
        </DialogHeader>

        <div className="space-y-5 text-sm">
          <p className="rounded-md bg-muted/10 p-3 leading-relaxed">{guide.purpose}</p>

          {guide.where && guide.where.length > 0 && (
            <Section heading="Trên màn có gì, ở đâu">
              <GuideBulletList items={guide.where} />
            </Section>
          )}

          {guide.howItWorks && guide.howItWorks.length > 0 && (
            <Section heading="Cách trang này chạy">
              <GuideBulletList items={guide.howItWorks} />
            </Section>
          )}

          {blocks.length > 0 && <GuideBlockList blocks={blocks} />}

          {guide.statuses && guide.statuses.length > 0 && (
            <Section heading="Các trạng thái nghĩa là gì">
              <GuideStatusList statuses={guide.statuses} />
            </Section>
          )}

          <p className="border-t border-border pt-3 text-muted">
            {guide.askWho ?? "Còn vướng chỗ nào thì hỏi quản lý trực tiếp của bạn."}
          </p>
        </div>
      </DialogContent>
    </Dialog>
  );
}

function Section({ heading, children }: { heading: string; children: React.ReactNode }) {
  return (
    <section>
      <h3 className="mb-2 font-semibold">{heading}</h3>
      {children}
    </section>
  );
}
